import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import {
  BLANK_RESOLUTION_TYPE,
  getResolutionTypeColor,
  getResolutionTypeLabel,
} from '../../services/resolutionTypeService';
import ChartCard from './ChartCard';
import { ANIMATION, formatNumber, toCount, useChartAnimation } from './chartTheme';

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.25rem;
  }
`;

const DonutWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
`;

const DonutCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
  padding: 0 3.5rem;
`;

const CenterValue = styled.span`
  color: var(--color-text-primary);
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
`;

const CenterLabel = styled.span`
  margin-top: 0.25rem;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 3.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.65rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  cursor: default;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.45 : 1)};
  background: ${({ $active }) => ($active ? 'var(--color-hover-overlay)' : 'transparent')};
  transition: opacity 0.15s ease, background-color 0.15s ease;

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-text-primary);
  }

  .count {
    color: var(--color-text-primary);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .pct {
    text-align: right;
    color: var(--color-text-muted);
    font-variant-numeric: tabular-nums;
  }
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

const SupportResolutionBreakdownChart = ({
  data = [],
  loading = false,
  error = false,
  onRetry = null,
  periodLabel = null,
}) => {
  const animate = useChartAnimation();
  const [activeKey, setActiveKey] = useState(null);

  const chartData = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const total = rows.reduce((sum, row) => sum + toCount(row?.count), 0);

    return rows
      .map((row) => {
        const rawType = row?.resolutionType;
        const type = rawType == null || String(rawType).trim() === ''
          ? BLANK_RESOLUTION_TYPE
          : String(rawType).toUpperCase();
        const value = toCount(row?.count);
        return {
          resolutionType: type,
          label: getResolutionTypeLabel(type) || type,
          count: value,
          percent: total > 0 ? ((value / total) * 100).toFixed(1).replace('.', ',') : '0',
          fill: getResolutionTypeColor(type),
        };
      })
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const totalTickets = useMemo(
    () => chartData.reduce((sum, row) => sum + row.count, 0),
    [chartData]
  );

  const activeItem = chartData.find((row) => row.resolutionType === activeKey) ?? null;

  return (
    <ChartCard
      title="Motivos de resolução"
      description={[
        'Tickets de Suporte/Bug resolvidos ou fechados',
        periodLabel ? `Período: ${periodLabel}` : null,
      ]}
      loading={loading}
      error={error}
      onRetry={onRetry}
      isEmpty={chartData.length === 0}
      emptyTitle="Nenhum ticket de suporte resolvido"
      emptyDescription="Não há tickets de Suporte/Bug resolvidos ou fechados no período selecionado."
      skeleton="donut"
    >
      <Layout>
        <DonutWrap>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius="70%"
                outerRadius="100%"
                paddingAngle={chartData.length > 1 ? 1.5 : 0}
                cornerRadius={4}
                stroke="var(--color-bg-primary)"
                strokeWidth={2}
                onMouseEnter={(_, index) => setActiveKey(chartData[index]?.resolutionType ?? null)}
                onMouseLeave={() => setActiveKey(null)}
                isAnimationActive={animate}
                {...ANIMATION}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={entry.resolutionType}
                    fill={entry.fill}
                    fillOpacity={activeKey && activeKey !== entry.resolutionType ? 0.3 : 1}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <DonutCenter aria-live="polite">
            <CenterValue>{formatNumber(activeItem ? activeItem.count : totalTickets)}</CenterValue>
            <CenterLabel>
              {activeItem ? `${activeItem.label} · ${activeItem.percent}%` : 'tickets resolvidos'}
            </CenterLabel>
          </DonutCenter>
        </DonutWrap>

        <List aria-label="Distribuição por motivo de resolução">
          {chartData.map((row) => (
            <Row
              key={row.resolutionType}
              $active={activeKey === row.resolutionType}
              $dimmed={activeKey != null && activeKey !== row.resolutionType}
              onMouseEnter={() => setActiveKey(row.resolutionType)}
              onMouseLeave={() => setActiveKey(null)}
            >
              <Swatch $color={row.fill} aria-hidden />
              <span className="label" title={row.label}>{row.label}</span>
              <span className="count">{formatNumber(row.count)}</span>
              <span className="pct">{row.percent}%</span>
            </Row>
          ))}
        </List>
      </Layout>
    </ChartCard>
  );
};

SupportResolutionBreakdownChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      resolutionType: PropTypes.string,
      count: PropTypes.number,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  periodLabel: PropTypes.string,
};

export default SupportResolutionBreakdownChart;
